import axios from "../axios";
import {
  Invoice,
  Payment,
  PaymentHistoryMeta,
  PaymentOverview,
  MinimumServicePrices,
  TableActiveState,
  TotalPaymentsPerDay,
  UserPaymentAccountData,
  UserPaymentLinksData,
  WalletSummary,
} from "../../pages/Payment/payment.types";
import {
  getPaymentLinksRequestParamsFormatter,
  getPaymentsHistoryRequestParamsFormatter,
  getPaymentsHistoryResponseFormatter,
  getPaymentsOverviewRequestParamsFormatter,
  getTotalPaymentsPerDayRequestParamsFormatter,
} from "../formatters/paymentFormatter";

export const getPaymentAccounts = async (): Promise<UserPaymentAccountData> => {
  try {
    const response = await axios.get("api/payout-accounts");

    return response.data.data;
  } catch (err) {
    throw new Error(err + " | Failed to fetch payment accounts");
  }
};

export const connectStripePaymentAccount = async (stripeConnectedAccountId: string) => {
  try {
    const response = await axios.put("api/payment/account", { stripeConnectedAccountId });
    return response.data.data.message;
  } catch (err) {
    throw new Error(err + " |  Faild to connect stripe payment account");
  }
};

export const createPaymentLink = async (body: any) => {
  try {
    const response = await axios.post("api/payment/link", body);
    return response.data.link;
  } catch (err) {
    throw new Error(err + " |  Failed to create payment link");
  }
};

export const createStripeAccountId = async () => {
  try {
    const response = await axios.post("api/payment/stripe-account");

    return response.data.data.stripeAccountId;
  } catch (err) {
    throw new Error(err + " |  Failed to create stripe account id");
  }
};

export const createStripeClientSecret = async (stripeConnectedAccountId: string) => {
  try {
    const response = await axios.post("api/payment/stripe-account-session", { stripeConnectedAccountId });
    return response.data.client_secret;
  } catch (err) {
    throw new Error(err + " |  Failed to create stripe client secret");
  }
};

export const getPaymentLinks = async ({
  page,
  searchValue,
  minDate,
  maxDate,
  status,
}: {
  page: number;
  searchValue?: string;
  minDate: Date | null;
  maxDate: Date | null;
  status: string;
}): Promise<UserPaymentLinksData> => {
  const LIMIT = 20;

  try {
    const queryParams = getPaymentLinksRequestParamsFormatter({
      page,
      limit: LIMIT,
      searchValue,
      minDate,
      maxDate,
      status,
    });

    const response = await axios.get(`api/payment/link${queryParams}`);

    return response.data;
  } catch (err) {
    throw new Error(err + " | Failed to fetch payment links");
  }
};

export const deletePaymentLink = async ({ linkId }: { linkId: number }) => {
  try {
    const response = await axios.delete(`api/payment/link/${linkId}`);
  } catch (err) {
    throw new Error(err + " | Failed to delete payment link");
  }
};

export const getPaymentsHistory = async ({
  page,
  search,
  status,
}: {
  page: number;
  search?: string;
  status?: TableActiveState;
}): Promise<{ items: Payment[]; meta: PaymentHistoryMeta }> => {
  const LIMIT = 5;

  try {
    const queryParams = getPaymentsHistoryRequestParamsFormatter({
      page,
      limit: LIMIT,
      search,
      status,
    });

    const response = await axios.get(`api/payment/link/history${queryParams}`);
    return getPaymentsHistoryResponseFormatter(response.data);
  } catch (err) {
    throw new Error(err + " | Failed to fetch payment history");
  }
};

export const getPaymentsOverview = async (): Promise<PaymentOverview> => {
  try {
    const response = await axios.get("api/payment/link/overview");
    return response.data;
  } catch (err) {
    throw new Error(err + " | Failed to fetch payment overview");
  }
};

export const getTotalPaymentsPerDay = async ({
  dateRange,
}: {
  dateRange: [Date | null, Date | null];
}): Promise<TotalPaymentsPerDay> => {
  try {
    const [minDate, maxDate] = dateRange;
    const queryParams = getTotalPaymentsPerDayRequestParamsFormatter(minDate, maxDate);

    const response = await axios.get(`api/payment/link/total-payments-per-day${queryParams}`);
    return response.data;
  } catch (err) {
    throw new Error(err + " | Failed to fetch total payments per day");
  }
};

export const getInvoiceDetails = async (transactionId: number | null): Promise<Invoice | undefined> => {
  if (!transactionId) return;
  try {
    const response = await axios.get(`api/payment/link/transactions/${transactionId}/invoice`);
    return response.data;
  } catch (err) {
    throw new Error(err + " | Failed to fetch invoice details");
  }
};

export const createPaymentAccount = async (body: any) => {
  try {
    const response = await axios.post("api/payout-accounts", body);
    return response.data.link;
  } catch (err) {
    throw new Error(err + " |  Failed to create payment account");
  }
};

export const updatePaymentAccount = async (payoutAccountId: number, body: any) => {
  try {
    const response = await axios.put(`api/payout-accounts/${payoutAccountId}`, body);
    return response.data;
  } catch (err) {
    throw new Error(err + " |  Failed to update payment account");
  }
};

export const getMinimumServicePrices = async (): Promise<MinimumServicePrices> => {
  try {
    const response = await axios.get("api/services/minimum-service-prices");

    return response.data.data;
  } catch (err) {
    throw new Error(err + " | Failed to fetch minimum service prices");
  }
};

export const getWalletSummary = async (): Promise<WalletSummary> => {
  try {
    const response = await axios.get("/users/wallet/summary");
    return response.data.data;
  } catch (err) {
    throw new Error(err + " | Failed to fetch wallet summary");
  }
};
